import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  LinearProgress,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area
} from 'recharts';
import {
  Assessment,
  TrendingUp,
  Group,
  Schedule,
  CheckCircle,
  PlayArrow,
  PauseCircle,
  ErrorOutline,
  Refresh,
  CalendarToday,
  BarChart as BarChartIcon
} from '@mui/icons-material';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const TrainingReportDashboard = ({ data = [], period = 'yearly' }) => {
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Fetch data or use prop data
  useEffect(() => {
    if (data && data.length > 0) {
      setTrainings(data)
      setLoading(false)
    } else {
      fetchTrainingData()
    }
  }, [data])

  // Fetch training data
  const fetchTrainingData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      setLoading(true);
      setError('');
      
      const response = await axios.get(`${API_URL}/api/employee-report/summary?period=${period}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      
      if (response.data.status === "success" && response.data.data) {
        setTrainings(response.data.data.trainings || []);
      } else {
        throw new Error('Failed to load training data');
      }
    } catch (err) {
      //console.error('Error fetching training data:', err);
      setError(err.message || 'Failed to load training data');
      setTrainings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchTrainingData();
    setRefreshing(false);
  };

  // Filter data based on date range
  const getFilteredData = () => {
    if (dateRange === 'all' || !trainings.length) return trainings;
    
    const now = new Date();
    const filterDate = new Date();
    
    switch (dateRange) {
      case '30':
        filterDate.setDate(now.getDate() - 30);
        break;
      case '90':
        filterDate.setDate(now.getDate() - 90);
        break;
      case '180':
        filterDate.setDate(now.getDate() - 180);
        break;
      default:
        return trainings;
    }
    
    return trainings.filter(training => {
      const trainingDate = new Date(training.startDate || training.createdAt);
      return trainingDate >= filterDate;
    });
  };

  const filteredTrainings = getFilteredData();
  
  // Filter out completed trainings for the table
  const nonCompletedTrainings = filteredTrainings.filter(training => 
    training.status?.toLowerCase() !== 'completed'
  );

  // Calculate statistics
  const stats = {
    total: filteredTrainings.length,
    completed: filteredTrainings.filter(t => t.status === 'Completed').length,
    inProgress: filteredTrainings.filter(t => t.status === 'In Progress').length,
    planned: filteredTrainings.filter(t => t.status === 'Planned').length,
    cancelled: filteredTrainings.filter(t => t.status === 'Cancelled').length,
    averageProgress: filteredTrainings.length > 0 
      ? Math.round(filteredTrainings.reduce((sum, t) => sum + (t.progressPercentage || 0), 0) / filteredTrainings.length)
      : 0
  };

  // Status distribution data for pie chart
  const statusData = [
    { name: 'Completed', value: stats.completed, color: '#4caf50' },
    { name: 'In Progress', value: stats.inProgress, color: '#2196f3' },
    { name: 'Planned', value: stats.planned, color: '#ff9800' },
    { name: 'Cancelled', value: stats.cancelled, color: '#f44336' }
  ].filter(item => item.value > 0);

  // Category distribution (removed from chart but kept for summary stats)
  const categoryData = filteredTrainings.reduce((acc, training) => {
    const category = training.skillCategory || 'Unknown';
    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {});

  // Monthly trend data
  const monthlyData = filteredTrainings.reduce((acc, training) => {
    const date = new Date(training.startDate || training.createdAt);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    
    if (!acc[monthKey]) {
      acc[monthKey] = { month: monthKey, started: 0, completed: 0 };
    }
    
    acc[monthKey].started += 1;
    if (training.status === 'Completed') {
      acc[monthKey].completed += 1;
    }
    
    return acc;
  }, {});

  const trendData = Object.values(monthlyData)
    .sort((a, b) => a.month.localeCompare(b.month))
    .slice(-6); // Last 6 months

  // Progress distribution
  const progressRanges = {
    '0-25%': filteredTrainings.filter(t => (t.progressPercentage || 0) <= 25).length,
    '26-50%': filteredTrainings.filter(t => (t.progressPercentage || 0) > 25 && (t.progressPercentage || 0) <= 50).length,
    '51-75%': filteredTrainings.filter(t => (t.progressPercentage || 0) > 50 && (t.progressPercentage || 0) <= 75).length,
    '76-100%': filteredTrainings.filter(t => (t.progressPercentage || 0) > 75).length
  };

  const progressData = Object.entries(progressRanges).map(([range, count]) => ({
    range,
    count,
    percentage: stats.total > 0 ? Math.round((count / stats.total) * 100) : 0
  }));

  // Handle table pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'success';
      case 'in progress': return 'info';
      case 'planned': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Summary Stats Cards - Moved to top */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="600">
            Summary Statistics
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                <Typography variant="h5" color="success.main" fontWeight="bold">
                  {stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0}%
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Completion Rate
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                <Typography variant="h5" color="info.main" fontWeight="bold">
                  {Object.keys(categoryData).length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Categories
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                <Typography variant="h5" color="info.main" fontWeight="bold">
                  {stats.inProgress}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  In Progress
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                <Typography variant="h5" color="error.main" fontWeight="bold">
                  {stats.cancelled}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Cancelled
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Status Distribution and Progress Distribution */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={7}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="600">
                Training Status Distribution
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {statusData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                  <Typography color="textSecondary">No data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ minHeight: 400, height: 'auto' }}>
            <CardContent sx={{ pb: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="600">
                Employee Performance Distribution
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Distribution of employees across different score ranges
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ mt: 2 }}>
                {progressData.map((item, index) => (
                  <Box key={index} sx={{ mb: 2.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body2" fontWeight="500">
                        Score Range: {item.range}
                      </Typography>
                      <Chip
                        label={`${item.count} employees (${item.percentage}% of total)`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={item.percentage}
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        backgroundColor: '#f5f5f5',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                        }
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {item.percentage}% of all employees fall in this range
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Training Details Table (Excluding Completed Trainings) */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="600">
            Active Training Details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing only In Progress, Planned, and Cancelled trainings (Completed trainings are excluded)
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Training Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Employee Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Trainer</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {nonCompletedTrainings.length > 0 ? (
                  nonCompletedTrainings
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((training, index) => (
                      <TableRow key={index}>
                        <TableCell>{training.title || training.name || 'N/A'}</TableCell>
                        <TableCell>{training.skillCategory || 'N/A'}</TableCell>
                        <TableCell>{training.employee || training.name || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={training.status || 'Unknown'}
                            color={getStatusColor(training.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={training.progressPercentage || 0}
                              sx={{ width: 80, height: 6, borderRadius: 3 }}
                            />
                            <Typography variant="body2">
                              {training.progressPercentage || 0}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {training.startDate ? new Date(training.startDate).toLocaleDateString() : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {training.endDate ? new Date(training.endDate).toLocaleDateString() : 'N/A'}
                        </TableCell>
                        <TableCell>{training.trainer || 'N/A'}</TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="textSecondary" sx={{ py: 2 }}>
                        No active trainings found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={nonCompletedTrainings.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>
    </Container>
  );
};

export default TrainingReportDashboard;